/**
 * @file Context/SiteSettingsContext.jsx
 * @description React context that fetches site settings once on app load
 * and provides them to all components. Avoids repeated API calls.
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { getSiteSettings } from '../services/api';

const SiteSettingsContext = createContext(null);

// Default fallback values (used while loading or on error)
const DEFAULTS = {
  companyInfo: {
    name: 'Nikaba Travels',
    tagline: 'Wander Wisely, Travel Boldly',
    description: '',
    email: 'info@nikabatravels.com',
    phone: '+91 800 2406 800',
    whatsapp: '918002406800',
    address: { street: '34-A, DDA Flats, Mata Sundari Road', city: 'New Delhi', state: 'Delhi', country: 'India', postalCode: '110002' },
  },
  hero: {
    backgroundImage: '/ai-taj.webp',
    heading: 'Best Inbound & Outbound',
    headingHighlight: 'Tour Operator in India',
    subtitle: 'Discover extraordinary destinations, create unforgettable memories, and experience the journey of a lifetime',
    ctaPrimary: { text: 'Explore Tours', link: '/tours' },
    ctaSecondary: { text: 'Read Blogs', link: '/blogs' },
  },
  socialLinks: {
    facebook: 'https://facebook.com/nikabatravels',
    instagram: 'https://instagram.com/nikaba.travels',
    twitter: 'https://twitter.com/nikabatravels',
    linkedin: 'https://linkedin.com/company/nikabatravels',
    youtube: '',
  },
  footer: {
    aboutText: "Crafting unforgettable journeys across India's most beautiful destinations. Your trusted partner in creating memories that last a lifetime.",
    copyrightText: '© {year} Nikaba Travels. All rights reserved.',
    quickLinks: [
      { label: 'About Us', url: '/about' },
      { label: 'Our Tours', url: '/tours' },
      { label: 'Services', url: '/services' },
      { label: 'Travel Blog', url: '/blogs' },
      { label: 'Contact', url: '/contact' },
    ],
    popularDestinations: [
      { label: 'Kashmir', url: '/tours' },
      { label: 'Ladakh', url: '/tours' },
      { label: 'Goa', url: '/tours' },
      { label: 'Rajasthan', url: '/tours' },
      { label: 'Kerala', url: '/tours' },
    ],
    bottomLinks: [
      { label: 'About Us', url: '/about' },
      { label: 'Privacy Policy', url: '/privacy' },
      { label: 'Terms & Conditions', url: '/terms' },
      { label: 'Support', url: '/contact' },
    ],
    trustBadges: [
      { value: '500+', label: 'Happy Travelers' },
      { value: '50+', label: 'Destinations' },
      { value: '4.8★', label: 'Average Rating' },
      { value: '24/7', label: 'Support' },
    ],
  },
  seo: {
    defaultTitle: 'Best Inbound and Outbound Tour Operator in India',
    defaultDescription: "Discover the world with Nikaba Travels - India's leading tour operator.",
    defaultKeywords: 'travel agency, tour operator, india tours',
  },
  campusAmbassador: {
    enabled: true,
    formLink: 'https://forms.gle/Raxb19mxFZXLvffV8',
    heading: 'Campus Ambassador Program',
    description: 'Join our exclusive Campus Ambassador Program and unlock amazing rewards.',
  },
  contactForm: {
    recipientEmail: 'nikabatravels@gmail.com',
    autoReplySubject: 'Thanks for contacting Nikaba Travels!',
    autoReplyMessage: "We've received your message and will get back to you within 24 hours.",
  },
};

export const SiteSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);

  const fetchSettings = async () => {
    try {
      const res = await getSiteSettings();
      const data = res?.data?.data?.settings || res?.data?.settings;
      if (data) setSettings(data);
    } catch (err) {
      console.error('Failed to load site settings, using defaults:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  return (
    <SiteSettingsContext.Provider value={{ settings, loading, refetch: fetchSettings }}>
      {children}
    </SiteSettingsContext.Provider>
  );
};

export const useSiteSettings = () => {
  const ctx = useContext(SiteSettingsContext);
  if (!ctx) return { settings: DEFAULTS, loading: false, refetch: () => {} };
  return ctx;
};

export default SiteSettingsContext;
